import React, {ReactNode} from "react";
import {Dialog as MuiDialog} from "@mui/material";
import {useModal} from "@ebay/nice-modal-react";
import {InputUtils, useMobile} from "../../helpers";
import {Button} from "./Button";
import {CloseDialogButton} from "./CloseDialogButton";
import {Container} from "./Container";
import {Text} from "./Text";
import {Theme} from "@mui/material/styles";
import {SxProps} from "@mui/system";

interface CustomDialogProps {
  title: ReactNode;
  forceTitle?: boolean;
  onClick: () => void | Promise<void>;
  onClose?: () => void;
  buttonText?: ReactNode;
  secondaryButtonText?: string;
  onClickSecondary?: () => void;
  isError?: boolean;
  width?: string;
  alignCenter?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  noButton?: boolean;
  autoClose?: boolean;
  sx?: SxProps<Theme>;
  header?: ReactNode;
  footer?: ReactNode;
  gap?: string;
}

export const CustomDialog = ({
  title,
  buttonText = "Save",
  onClick,
  onClose,
  secondaryButtonText,
  onClickSecondary,
  alignCenter = false,
  width,
  isError,
  disabled = false,
  children,
  forceTitle = false,
  noButton = false,
  autoClose = true,
  sx,
  header,
  footer,
  gap = "24px",
}: CustomDialogProps) => {
  const isMobile = useMobile();
  const modal = useModal();
  const onClickModal = async () => {
    onClick();
    if (autoClose) {
      modal.resolve(true);
      modal.hide();
    }
  };

  const onClickSecondaryModal = async () => {
    onClickSecondary?.();
    modal.resolve(false);
    modal.hide();
  };

  return (
    <MuiDialog
      open={modal.visible}
      TransitionProps={{
        onExited: () => {
          onClose?.();
          modal.remove();
        },
      }}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          padding: isMobile ? "44px 24px 24px 24px" : "44px",
          gap,
          "&.MuiPaper-elevation": {
            margin: "12px",
          },
        },
        ...sx,
      }}
    >
      <CloseDialogButton onClose={modal.hide} />
      {header && <Container>{header}</Container>}
      <Container sx={{gap: "16px", width: width || "300px", alignItems: alignCenter ? "center" : "start"}}>
        <Text variant={"interDisplay600"} size={24}>
          {!forceTitle && typeof title === "string" ? InputUtils.prettierSnakeCaseSentence(title) : title}
        </Text>
        {children}
      </Container>
      <Container sx={{gap: "8px"}}>
        {!noButton && (
          <Button
            buttonType={isError ? "contained-error" : "contained-primary"}
            onClick={onClickModal}
            disabled={disabled}
            fullWidth
          >
            {typeof buttonText === "string" ? InputUtils.prettierSnakeCaseSentence(buttonText) : buttonText}
          </Button>
        )}
        {secondaryButtonText && (
          <Button
            buttonType={isError ? "outlined-secondary" : "contained-secondary"}
            onClick={onClickSecondaryModal}
            fullWidth
          >
            {InputUtils.prettierSnakeCaseSentence(secondaryButtonText)}
          </Button>
        )}
        {footer && <Container fullWidth>{footer}</Container>}
      </Container>
    </MuiDialog>
  );
};
